import type { Card } from "../gen/bazivan/v1/table_pb";
import { fa, rankLabel, suitSymbol } from "../game/cards";
import { inCubic, inOutCubic, lerp, outBack, outCubic } from "../scene/ease";
import { flyCard, kill, tween, tweenRaw, wait, waitRaw } from "../scene/fly";
import {
  CARD_ASPECT, CHAIR_RING, cardW, handCardTarget, place, playerCardW, scatter, seatAngleDeg, seatPos, type Vec,
} from "../scene/geometry";
import { TableView } from "../scene/table";
import { forbidden, isGoldTen, isPrize, sameCard, unitOf, type CbLocal } from "./state";

/**
 * صحنه‌ی چهاربرگ — پورتِ CharBargView.cs روی TableView.
 *
 * لایه‌ها به ترتیبِ Build(): Table → OppHands → Pool (زمین) → Piles (دسته‌های
 * جمع‌شده + نشانِ سور) → Seats → Hand → Fly → Ui → Overlays. زمین و دسته‌ها هر
 * دو زیرلایه‌ی «piles»ِ پایه‌اند تا ترتیبِ رسم همان بمانَد.
 */
export type CbCallbacks = { onCard(i: number): void; seatName(l: number): string };

const GOLD_BRIGHT = "#ffd84a", TEAL = "#3aa6a0";

export class CbScene extends TableView {
  private cb: CbCallbacks;
  private lastSt: CbLocal | null = null;
  private awaitingHuman = false;
  private pool!: HTMLElement; private pilesLayer!: HTMLElement;

  constructor(host: HTMLElement, count: number, cb: CbCallbacks) {
    super(host, count, "cbg");
    this.cb = cb;
    this.build();
  }

  protected seatDFactor(): number { return 0.26; }
  protected plateH(): number { return 58; }

  private get pcardW(): number { return playerCardW(this.sc); }
  private get poolW(): number { return cardW(this.sc) * 2.15; }   // کارت‌های وسط درشت و خوانا

  protected buildCenter(): void {
    this.pool = document.createElement("div"); this.pool.className = "tiles pool";
    this.pilesLayer = document.createElement("div"); this.pilesLayer.className = "tiles cbpiles";
    this.piles.append(this.pool, this.pilesLayer);
    // دسته‌ی کشیدن — گوشه‌ی مرکز، فقط برای مبدأِ انیمیشنِ پخش
    const deck = this.deck();
    for (let i = 0; i < 3; i++) {
      const c = this.cardEl(null, true, cardW(this.sc));
      c.style.transform = place({ x: deck.x - i * 2, y: deck.y + i * 2 }, i * 2 - 2);
      this.pool.appendChild(c);
    }
  }

  /** دسته‌ی چهاربرگ در ۰٫۶۶R است، نه ۰٫۲۸ِ حکم. */
  protected override deck(): Vec { return { x: -this.sc.tableR * 0.66 * this.sc.aspect, y: 0 }; }

  protected redraw(): void {
    const st = this.lastSt; if (!st) return;
    this.layoutSeats(st); this.render(st); this.renderTable(st);
  }

  setAwaitingHuman(v: boolean): void { this.awaitingHuman = v; }

  // ---------------------------------------------------------------- نشست‌ها

  layoutSeats(st: CbLocal): void {
    this.lastSt = st;
    const names = st.names.map((nm, l) => (l !== 0 && st.teams && unitOf(st, l) === unitOf(st, 0) ? `${nm} (یار)` : nm));
    this.layoutSeatsFrom({ names, bots: st.bots, avatars: st.avatars, subscribers: st.subscribers, places: st.places, connected: st.connected, watching: st.watching });
    // شمارِ جمع‌شده‌های خودم زیرِ پلاکِ نام
    const me = this.seatEls[0];
    if (me) {
      const c = document.createElement("div"); c.className = "mecnt";
      c.style.top = `calc(100% + ${me.d * 0.1 + 30}px)`;
      me.root.appendChild(c);
      me.count = c;
    }
  }

  // ---------------------------------------------------------------- رندر

  render(st: CbLocal): void {
    this.lastSt = st;
    this.renderPlayerHand(st);
    this.renderOpponentHands(st);
    this.renderPiles(st);
    for (let p = 0; p < this.count; p++) {
      this.setRing(p, !st.roundOver && st.turn === p);
      const s = this.seatEls[p];
      // عدد هم مالِ همان دسته‌ای است که کنارش دیده می‌شود: در بازیِ تیمی جمعِ تیم.
      if (s?.count) s.count.textContent = `${fa(this.pileCountOf(st, p))} برگ`;
      this.setConnected(p, !!st.connected[p]);
    }
  }

  /** جای دسته‌ی جمع‌شده‌ی یک صندلی — کمی به‌سمتِ مرکزِ میز، کنارِ آواتار. */
  /** شمارِ برگ‌هایی که کنارِ این نشست شمرده می‌شود — تیمی که باشد، جمعِ تیم. */
  private pileCountOf(st: CbLocal, seat: number): number {
    if (!st.teams) return st.piles[seat] ?? 0;
    const u = unitOf(st, seat);
    let n = 0;
    for (let s2 = 0; s2 < this.count; s2++) if (unitOf(st, s2) === u) n += st.piles[s2] ?? 0;
    return n;
  }

  /** دسته‌ی این نشست کجا جمع می‌شود — در بازیِ تیمی، دسته‌ی تیمش. */
  private pileSeatOf(seat: number): number {
    const st = this.lastSt;
    if (!st || !st.teams) return seat;
    const u = unitOf(st, seat);
    for (let s = 0; s < this.count; s++) if (unitOf(st, s) === u) return s;
    return seat;
  }

  private pileAnchor(seat: number): Vec {
    if (seat === 0) return { x: -this.sc.tableR * 0.34 * this.sc.aspect, y: -this.sc.halfH + cardW(this.sc) * CARD_ASPECT * 0.9 };
    const p = this.seatEls[seat]?.pos ?? seatPos(this.sc, seat, this.count, this.sc.tableR * CHAIR_RING);
    return { x: p.x * 0.6, y: p.y * 0.6 };
  }

  /** دسته‌ی پشت‌ورو (یک‌جا، تا ۶ پشت برای ضخامت) و هر سور یک کارتِ رو و ۹۰درجه رویش. */
  /**
   * دسته‌های برد را به صاحبشان می‌بندد.
   *
   * در بازیِ تیمی بردها مالِ تیم‌اند، نه تک‌تکِ نفرها؛ پس یک دسته برای هر تیم
   * می‌ماند، جلوی نخستین نشستِ همان تیم — دسته‌ی ما پایین و دسته‌ی حریف سمتِ
   * خودشان. در بازیِ تکی هر کس دسته‌ی خودش را دارد.
   */
  private pileGroups(st: CbLocal): { seat: number; total: number; surs: Card[] }[] {
    if (!st.teams) {
      return Array.from({ length: this.count }, (_, seat) => ({
        seat, total: st.piles[seat] ?? 0, surs: st.surCards[seat] ?? [],
      }));
    }
    const groups = new Map<number, { seat: number; total: number; surs: Card[] }>();
    for (let seat = 0; seat < this.count; seat++) {
      const u = unitOf(st, seat);
      const g = groups.get(u) ?? { seat, total: 0, surs: [] };
      g.total += st.piles[seat] ?? 0;
      g.surs = g.surs.concat(st.surCards[seat] ?? []);
      groups.set(u, g);
    }
    return [...groups.values()];
  }

  private renderPiles(st: CbLocal): void {
    this.pilesLayer.innerHTML = "";
    if (this.handHidden) return;
    const w = cardW(this.sc) * 0.82;
    for (const { seat, total, surs } of this.pileGroups(st)) {
      if (total === 0 && surs.length === 0) continue;
      const anchor = this.pileAnchor(seat);
      const baseRot = seatAngleDeg(seat, this.count) + 90, rad = baseRot * Math.PI / 180;
      const along = { x: Math.cos(rad), y: Math.sin(rad) };
      const backs = Math.min(6, Math.max(1, total));
      for (let i = 0; i < backs; i++) {
        const c = this.cardEl(null, true, w);
        const jo = scatter(i, w * 0.3, w * 0.3, 9);
        c.style.transform = place({ x: anchor.x + i * 1.4 + jo.dx, y: anchor.y + i * 1.8 + jo.dy }, baseRot + jo.rot);
        this.pilesLayer.appendChild(c);
      }
      surs.forEach((card, j) => {
        const f = this.cardEl(card, false, w);
        f.classList.add("surmark"); f.dataset.seat = String(seat);
        const m = (j - (surs.length - 1) / 2) * w * 0.5;
        f.style.transform = place({ x: anchor.x + along.x * m + 2, y: anchor.y + along.y * m + 3 }, baseRot + 90);
        this.pilesLayer.appendChild(f);
      });
    }
  }

  private renderPlayerHand(st: CbLocal): void {
    this.hand.innerHTML = "";
    if (this.handHidden) return;
    const hand = st.myHand, n = hand.length;
    if (n === 0) return;
    const w = this.pcardW;
    const maxSpan = this.sc.halfW * 2 * 0.7 - w;
    const step = n > 1 ? Math.min(w * 0.9, maxSpan / (n - 1)) : 0;
    const myTurn = this.awaitingHuman;
    hand.forEach((card, i) => {
      const m = i - (n - 1) / 2;
      const face = this.cardEl(card, false, w);
      face.classList.add("mine");
      let y = -this.sc.halfH + w * CARD_ASPECT * 0.85 + (myTurn ? w * 0.12 : 0);
      if (myTurn) {
        const ok = !forbidden(st, card);
        if (ok) face.classList.add("playable");
        else { face.classList.add("forbidden"); y -= w * 0.12; }   // سرباز روی زمینِ خالی: کم‌رنگ و پایین‌تر
        this.armCard(face, this.cardLabelFor(card), () => this.cb.onCard(i));
      }
      face.style.transform = place({ x: m * step, y });
      face.style.zIndex = String(i);
      this.hand.appendChild(face);
    });
    this.armHand();
  }

  private renderOpponentHands(st: CbLocal): void {
    this.opp.innerHTML = "";
    if (this.handHidden) return;
    const cw = cardW(this.sc) * 0.9;
    // تماشاچی دستی ندارد، پس صندلیِ ۰ هم بادبزنِ پشت‌وروی خودش را می‌گیرد.
    for (let p = st.watching ? 0 : 1; p < this.count; p++) {
      const shown = st.counts[p] ?? 0;
      for (let j = 0; j < shown; j++) {
        const { pos, rot } = handCardTarget(this.sc, p, this.count, Math.max(shown, 1), j);
        const c = this.cardEl(null, true, cw);
        c.style.transform = place(pos, rot);
        c.style.zIndex = String(j);
        this.opp.appendChild(c);
      }
    }
  }

  // ---- استخرِ وسطِ میز ----

  /** پشته‌ی نامنظمِ وسطِ میز: با تعداد کمی گسترده می‌شود؛ seed=i پس بینِ رندرها ثابت. */
  private poolSlot(i: number, n: number): { pos: Vec; rot: number } {
    const grow = Math.min(2.2, Math.max(0.5, n * 0.14));
    const s = scatter(i, this.poolW * (1.1 + grow), this.poolW * (0.95 + grow * 0.85));
    return { pos: { x: s.dx, y: s.dy }, rot: s.rot };
  }

  renderTable(st: CbLocal): void {
    this.lastSt = st;
    this.pool.querySelectorAll(".card:not(.back)").forEach((c) => c.remove());
    const n = st.table.length;
    st.table.forEach((card, i) => {
      const face = this.cardEl(card, false, this.poolW);
      face.classList.add("poolcard");
      face.dataset.card = `${card.suit}-${card.rank}`;
      const { pos, rot } = this.poolSlot(i, n);
      face.style.transform = place(pos, rot);
      face.dataset.rot = String(rot); face.dataset.x = String(pos.x); face.dataset.y = String(pos.y);
      face.style.zIndex = String(10 + i);
      this.pool.appendChild(face);
    });
  }

  // ---------------------------------------------------------------- انیمیشن‌ها

  /**
   * AnimDealBatch: اول کارت‌های زمین (فقط سرِ شروعِ دور)، بعد دست‌ها از
   * شروع‌کننده. کارتِ زمین درشت است و کارتِ حریف کمی کوچک‌تر از کارتِ خودت —
   * همان اندازه‌هایی که CharBargView می‌دهد.
   */
  async animDeal(st: CbLocal, perPlayer: number, toTable: number, starter: number): Promise<void> {
    this.handHidden = true; this.render(st);
    const flying: HTMLElement[] = [];
    const deck = this.deck();

    for (let i = 0; i < toTable; i++) {
      const c = this.flyEl(null, true, this.poolW);
      flying.push(c);
      void flyCard(c, deck, this.poolSlot(i, Math.max(toTable, 1)).pos, 0, 240);
      await wait(50);
    }

    const pw = this.pcardW, cw = cardW(this.sc) * 0.9;
    for (let n = 0; n < perPlayer; n++) {
      for (let k = 0; k < this.count; k++) {
        const p = (starter + k) % this.count;
        let pos: Vec, rot: number;
        if (p === 0 && !st.watching) {
          pos = { x: (n - (perPlayer - 1) / 2) * pw * 0.9, y: -this.sc.halfH + pw * CARD_ASPECT * 0.85 };
          rot = 0;
        } else {
          const t = handCardTarget(this.sc, p, this.count, perPlayer, n);
          pos = t.pos; rot = t.rot;
        }
        const c = this.flyEl(null, true, p === 0 ? pw : cw);
        flying.push(c);
        void flyCard(c, deck, pos, rot, 240);
        await wait(45);
      }
    }

    await wait(260);
    flying.forEach(kill);
    this.handHidden = false; this.render(st); this.renderTable(st);
  }

  /** AnimPlay: از صندلی به وسطِ میز، ۲۶۰. */
  async animPlay(seat: number, card: Card): Promise<void> {
    const from = seat === 0 ? this.myOrigin() : this.seatFx(seat);
    const face = this.flyEl(card, false, this.poolW);
    await flyCard(face, from, { x: 0, y: 0 }, 0, 260);
    kill(face);
  }

  /** AnimCapture: کارتِ زده و برداشته‌ها از وسط به دسته‌ی جمع‌کننده، ۳۰۰. */
  async animCapture(seat: number, played: Card, captured: Card[]): Promise<void> {
    if (seat === 0) this.hitStop(60);   // ایستِ کوتاهِ گرفتنِ خودت
    const to = this.pileAnchor(this.pileSeatOf(seat));
    const faces = [played, ...captured].map((c) => this.flyEl(c, false, this.poolW));
    for (const f of faces) { void flyCard(f, { x: 0, y: 0 }, to, 0, 300); setTimeout(() => kill(f), Math.max(1, 360 * 3)); }
    await wait(360);
    faces.forEach(kill);
  }

  /** AnimPrizeOnTable: خودِ کارت را پیدا می‌کنیم (برچسب)، نه شماره‌ی خانه. */
  async animPrizeOnTable(slot: number, card: Card): Promise<void> {
    const faces = [...this.pool.querySelectorAll<HTMLElement>(".poolcard")];
    const face = faces.find((f) => f.dataset.card === `${card.suit}-${card.rank}`) ?? faces[slot];
    if (!face) return;
    await this.prizeBeat(face, isGoldTen(card));
  }

  /**
   * لحظه‌ی کارتِ امتیازدار: کارت روی همه می‌آید و بزرگ می‌شود، «برق» از رویش
   * می‌گذرد، هاله و ذرات، پلاکِ امتیاز بالایش — و بعد همه‌چیز به حالتِ پیش‌فرض.
   *   • ۱۰ خشت: طلا — دو برق، ستاره، «۱۰ خشت — ۳+ امتیاز». • ۲ گشنیز: فیروزه — یک برق.
   */
  private async prizeBeat(face: HTMLElement, gold: boolean): Promise<void> {
    const at = { x: Number(face.dataset.x ?? 0), y: Number(face.dataset.y ?? 0) }, rot0 = Number(face.dataset.rot ?? 0);
    face.style.zIndex = "99";
    const col = gold ? GOLD_BRIGHT : TEAL;
    face.classList.add("prize"); face.style.setProperty("--pc", col);
    const w = this.poolW, h = w * CARD_ASPECT;
    const shine = document.createElement("i"); shine.className = "shine";
    shine.style.width = `${w * 0.32}px`; shine.style.height = `${h * 1.7}px`;
    face.appendChild(shine);
    const label = document.createElement("div"); label.className = `cb-prizelbl ${gold ? "gold" : "teal"} in`;
    label.style.width = `${Math.min(this.sc.tableR, 300)}px`;
    label.textContent = gold ? "۱۰ خشت — ۳+ امتیاز" : "۲ گشنیز — ۲+ امتیاز";
    label.style.transform = place({ x: at.x, y: at.y + h * 0.68 + 26 });
    this.ov.appendChild(label);

    const target = gold ? 1.38 : 1.24;
    await tween(220, (k) => {
      const e = outBack(k);
      face.style.transform = place(at, lerp(rot0, 0, k), lerp(1, target, e));
      face.style.setProperty("--pa", String(outCubic(k)));
    });
    void this.pulse(at, col, w * 0.6, w * (gold ? 2.6 : 2.0), 550, 0.5);
    if (gold) void this.sparkle(at, w, 14);
    const sweeps = gold ? 2 : 1;
    for (let s = 0; s < sweeps; s++) {
      await tween(gold ? 360 : 420, (k) => {
        shine.style.transform = `translate(-50%,-50%) translateX(${lerp(-w * 0.9, w * 0.9, inOutCubic(k))}px) rotate(-24deg)`;
        shine.style.opacity = String(0.85 * Math.sin(k * Math.PI));
      });
      if (gold && s === 0) await wait(90);
    }
    await wait(gold ? 260 : 160);
    label.classList.remove("in"); label.classList.add("out");
    await tween(180, (k) => {
      const e = outCubic(k);
      face.style.transform = place(at, lerp(0, rot0, e), lerp(target, 1, e));
      face.style.setProperty("--pa", String(1 - k));
    });
    face.style.transform = place(at, rot0);
    face.classList.remove("prize"); kill(shine); kill(label);
  }

  /** ستاره‌های طلاییِ ریز که از کارت به بیرون می‌پاشند و محو می‌شوند (۷۰۰ خام). */
  private sparkle(center: Vec, spread: number, n: number): Promise<void> {
    const stars: { el: HTMLElement; p: Vec; v: Vec; dir: number }[] = [];
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, d = 8 + Math.random() * 8;
      const el = document.createElement("i"); el.className = "cb-spark"; el.textContent = "✦";
      el.style.fontSize = `${d}px`;
      const p = { x: center.x + Math.cos(a) * spread * 0.3, y: center.y + Math.sin(a) * spread * 0.3 };
      const sp = spread * (1.2 + Math.random() * 1.2);
      this.ov.appendChild(el);
      stars.push({ el, p, v: { x: Math.cos(a) * sp, y: Math.sin(a) * sp }, dir: i % 2 === 0 ? 1 : -1 });
    }
    let last = performance.now();
    return tweenRaw(700, (k) => {
      const now = performance.now(), dt = (now - last) / 1000; last = now;
      for (const s of stars) {
        s.p = { x: s.p.x + s.v.x * dt * (1 - k), y: s.p.y + s.v.y * dt * (1 - k) };
        s.el.style.transform = place(s.p, k * 360 * s.dir);
        s.el.style.opacity = String(1 - inCubic(k));
      }
    }).then(() => stars.forEach((s) => kill(s.el)));
  }

  /**
   * AnimSurCancel: نشانِ ۹۰درجه‌ی حریف روی دسته‌اش ترک می‌خورد، سرخ می‌شود، بعد
   * می‌چرخد و برمی‌گردد و پشت‌ورو زیرِ دسته می‌رود؛ کپسولِ «سور در شد» بالای زمین.
   */
  async animSurCancel(seat: number, played: Card, captured: Card[], victim: number, cancelled: Card | null): Promise<void> {
    const victimPile = this.pileSeatOf(victim);
    let mark = [...this.pilesLayer.querySelectorAll<HTMLElement>(`.surmark[data-seat="${victimPile}"]`)].pop() ?? null;
    if (!mark && cancelled) {
      mark = this.cardEl(cancelled, false, cardW(this.sc) * 0.82);
      mark.classList.add("surmark");
      mark.style.transform = place(this.pileAnchor(victimPile), seatAngleDeg(victimPile, this.count) + 180);
      this.pilesLayer.appendChild(mark);
    }
    const cap = document.createElement("div"); cap.className = "cb-cap in";
    cap.style.width = `${Math.min(this.sc.tableR * 1.1, this.sc.halfW * 1.7)}px`;
    cap.style.transform = place({ x: 0, y: this.poolW * CARD_ASPECT * 0.62 + 30 });
    const who = this.cb.seatName(victim);
    cap.textContent = seat === 0 ? `سور در شد! سورِ ${who} باطل شد`
      : victim === 0 ? `سورت در شد! ${this.cb.seatName(seat)} سورت را باطل کرد` : `سور در شد! سورِ ${who} باطل شد`;
    this.ov.appendChild(cap);

    if (mark) {
      mark.style.zIndex = "50";
      const m0 = this.currentPos(mark), r0 = rotOf(mark);
      // لرزش + ترک‌ها یکی‌یکی
      const cracks = document.createElement("i"); cracks.className = "cracks"; mark.appendChild(cracks);
      const mw = cardW(this.sc) * 0.82, mh = mw * CARD_ASPECT;
      const hit = { x: mw * 0.12, y: mh * 0.08 };
      const angs = [25, 118, 200, 292, 70, 250], lens = [0.62, 0.55, 0.5, 0.6, 0.35, 0.4];
      void tweenRaw(400, (k) => { const a = (1 - k) * 6; mark!.style.translate = `${(Math.random() * 2 - 1) * a}px ${(Math.random() * 2 - 1) * a}px`; }).then(() => { mark!.style.translate = ""; });
      for (let i = 0; i < angs.length; i++) {
        const len = lens[i]! * mh;
        const line = document.createElement("b");
        line.style.cssText = `height:${i < 4 ? 2.2 : 1.4}px;transform:translate(${hit.x}px,${-hit.y}px) rotate(${-angs[i]!}deg)`;
        cracks.appendChild(line);
        if (i < 4) {
          const rad = angs[i]! * Math.PI / 180;
          const br = document.createElement("b");
          const bp = { x: hit.x + Math.cos(rad) * len * 0.55, y: hit.y + Math.sin(rad) * len * 0.55 };
          br.style.cssText = `height:1.4px;width:${len * 0.35}px;transform:translate(${bp.x}px,${-bp.y}px) rotate(${-(angs[i]! + (i % 2 === 0 ? 38 : -42))}deg)`;
          cracks.appendChild(br);
        }
        await tween(90, (k) => { line.style.width = `${lerp(0, len, outCubic(k))}px`; });
      }
      mark.classList.add("hurt");
      await waitRaw(420);
      mark.classList.remove("hurt");
      // برگشت: ۹۰درجه‌ی سور برمی‌گردد به راستای دسته و کارت پشت‌ورو زیرِ دسته می‌رود
      const baseRot = seatAngleDeg(victimPile, this.count) + 90;
      const p1 = this.pileAnchor(victimPile);
      await tween(300, (k) => {
        const e = inOutCubic(k);
        mark!.style.transform = place({ x: lerp(m0.x, p1.x, e), y: lerp(m0.y, p1.y, e) }, lerp(r0, baseRot, e));
      });
      const back = this.cardEl(null, true, mw);
      back.style.transform = place(p1, baseRot);
      await tween(150, (k) => { mark!.style.transform = `${place(p1, baseRot)} scaleX(${1 - k})`; });
      kill(mark);
      this.pilesLayer.prepend(back);
      back.style.transform = `${place(p1, baseRot)} scaleX(0)`;
      await tween(150, (k) => { back.style.transform = `${place(p1, baseRot)} scaleX(${k})`; });
      back.style.transform = place(p1, baseRot);
    } else await waitRaw(500);

    // جمعِ خودِ بازیکن: پروازِ عادی، بی‌جشن
    await this.animCapture(seat, played, captured);
    cap.classList.remove("in"); cap.classList.add("out");
    setTimeout(() => kill(cap), 300);
  }

  /** برای کپسولِ ترکیب‌ها (ری‌اکت) — نامِ کارت به فارسی. */
  static cardLabel(c: Card): string { return `${rankLabel(c.rank)}${suitSymbol[c.suit] ?? ""}`; }
  static isPrize = isPrize;
  static sameCard = sameCard;
}

/** زاویه‌ی فعلی از transform (درجه، در دستگاهِ یونیتی). */
function rotOf(el: HTMLElement): number {
  const m = /rotate\(([-\d.]+)deg\)/.exec(el.style.transform);
  return m ? -Number(m[1]) : 0;
}
